import { useEffect, useState } from 'react';
import { PageHero } from '@/components/PageHero';
import { ServiceCard } from '@/components/ServiceCard';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { FAQSection } from '@/components/FAQSection';
import { SectionDivider } from '@/components/SectionDivider';
import { SEO } from '@/components/SEO';
import { Skeleton } from '@/components/ui/skeleton';
import { getServices } from '@/lib/api';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';
import { SERVICES_PAGE } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';

export default function Services() {
  const [services, setServices] = useState(null);
  const [error, setError] = useState(false);

  const load = (attempt = 0) => {
    setError(false);
    getServices()
      .then(setServices)
      .catch(() => {
        // Transient network blips (e.g. a brief backend restart) shouldn't
        // look like missing services — retry a couple of times first.
        if (attempt < 2) setTimeout(() => load(attempt + 1), 800 * (attempt + 1));
        else setError(true);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={SERVICES_PAGE.root}>
      <SEO
        title="Services"
        description="Photography and videography services in the DMV: portraits, events, graduations, automotive shoots, and cinematic video production."
      />
      <PageHero
        kicker="What I Offer"
        title="SERVICES"
        subtitle="Professional photography and videography services tailored to your needs."
        backgroundImage={SITE_IMAGES.servicesHero}
        backgroundVideo={SITE_VIDEOS.servicesHero}
      />

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          {error && (
            <div className="text-center py-10">
              <p className="text-sm text-[hsl(var(--lxra-muted))]">Unable to load services right now. Please try again shortly.</p>
              <button
                onClick={() => load()}
                data-testid="services-retry-button"
                className="mt-4 inline-flex items-center h-10 px-5 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium"
              >
                Retry
              </button>
            </div>
          )}

          {!services && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid={SERVICES_PAGE.grid}>
              <SkeletonList count={6} className="h-56 rounded-2xl bg-[#141416]" />
            </div>
          )}

          {services && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid={SERVICES_PAGE.grid}>
              {services.map((svc, i) => (
                <Reveal key={svc.slug} delay={i * 0.05}>
                  <ServiceCard service={svc} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <SectionDivider />

      <FAQSection />

      <CTASection
        kicker="Ready to book your session?"
        title="LET'S CREATE SOMETHING AMAZING."
        testId="services-cta-banner"
        buttonTestId="services-cta-book-now-button"
      />
    </div>
  );
}
